/**
 * TransactionHistory Data Model
 * Complete record of all payments, adjustments, and account activities
 */

// Transaction types
export const TRANSACTION_TYPES = {
  PAYMENT: 'payment',
  ADJUSTMENT: 'adjustment',
  FEE: 'fee',
  CONTACT: 'contact',
  ARRANGEMENT: 'arrangement',
  INTEREST: 'interest',
  LEGAL_FEE: 'legal_fee',
  DEBT_FEE: 'debt_fee'
};

// Contact types for contact transactions
export const CONTACT_TYPES = {
  PHONE_CALL: 'phone_call',
  EMAIL: 'email',
  LETTER: 'letter',
  IN_PERSON: 'in_person',
  SMS: 'sms'
};

// Transaction validation rules
export const TRANSACTION_VALIDATION_RULES = {
  type: {
    required: true,
    allowedValues: Object.values(TRANSACTION_TYPES)
  },
  amount: {
    required: false, // Not all transactions have amounts (e.g., contact attempts)
    precision: 2
  },
  description: {
    required: true,
    minLength: 1,
    maxLength: 500
  },
  reference: {
    required: false,
    maxLength: 100
  },
  userId: {
    required: false,
    maxLength: 50
  }
};

/**
 * Creates a new TransactionHistory object
 * @param {Object} data - Initial transaction data
 * @returns {Object} TransactionHistory object
 */
export const createTransaction = (data = {}) => {
  return {
    id: data.id || generateTransactionId(),
    clientId: data.clientId || '',
    type: data.type || TRANSACTION_TYPES.PAYMENT,
    amount: data.amount ? roundToTwoDecimals(parseFloat(data.amount)) : 0,
    description: data.description || '',
    timestamp: data.timestamp || new Date(),
    userId: data.userId || 'system',
    reference: data.reference || '',
    contactType: data.contactType || null, // Only used for contact transactions
    metadata: data.metadata || {} // Additional transaction-specific data
  };
};

/**
 * Creates a payment transaction
 * @param {string} clientId - Client ID
 * @param {number} amount - Payment amount
 * @param {string} description - Payment description
 * @param {string} reference - Payment reference (check number, etc.)
 * @param {string} userId - User who recorded the payment
 * @returns {Object} Payment transaction
 */
export const createPaymentTransaction = (clientId, amount, description, reference = '', userId = 'system') => {
  return createTransaction({
    clientId,
    type: TRANSACTION_TYPES.PAYMENT,
    amount,
    description: description || `Payment of ${formatCurrency(amount)}`,
    reference,
    userId
  });
};

/**
 * Creates an adjustment transaction
 * @param {string} clientId - Client ID
 * @param {number} amount - Adjustment amount (can be negative)
 * @param {string} description - Adjustment description
 * @param {string} userId - User who made the adjustment
 * @returns {Object} Adjustment transaction
 */
export const createAdjustmentTransaction = (clientId, amount, description, userId = 'system') => {
  return createTransaction({
    clientId,
    type: TRANSACTION_TYPES.ADJUSTMENT,
    amount,
    description: description || `Account adjustment: ${formatCurrency(amount)}`,
    userId
  });
};

/**
 * Creates a contact transaction
 * @param {string} clientId - Client ID
 * @param {string} contactType - Type of contact (phone, email, etc.)
 * @param {string} description - Contact description
 * @param {string} userId - User who made the contact
 * @returns {Object} Contact transaction
 */
export const createContactTransaction = (clientId, contactType, description, userId = 'system') => {
  return createTransaction({
    clientId,
    type: TRANSACTION_TYPES.CONTACT,
    amount: 0,
    description,
    contactType,
    userId
  });
};

/**
 * Creates a payment arrangement transaction
 * @param {string} clientId - Client ID
 * @param {string} description - Arrangement description
 * @param {Object} arrangementDetails - Payment arrangement details
 * @param {string} userId - User who created the arrangement
 * @returns {Object} Arrangement transaction
 */
export const createArrangementTransaction = (clientId, description, arrangementDetails = {}, userId = 'system') => {
  return createTransaction({
    clientId,
    type: TRANSACTION_TYPES.ARRANGEMENT,
    amount: arrangementDetails.amount || 0,
    description,
    userId,
    metadata: arrangementDetails
  });
};

/**
 * Sorts transactions by timestamp (most recent first)
 * @param {Array} transactions - Array of transaction objects
 * @returns {Array} Sorted transactions
 */
export const sortTransactionsByDate = (transactions) => {
  return [...transactions].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
};

/**
 * Filters transactions by type
 * @param {Array} transactions - Array of transaction objects
 * @param {string} type - Transaction type to filter by
 * @returns {Array} Filtered transactions
 */
export const filterTransactionsByType = (transactions, type) => {
  return transactions.filter(transaction => transaction.type === type);
};

/**
 * Filters transactions by date range
 * @param {Array} transactions - Array of transaction objects
 * @param {Date} startDate - Start date (inclusive)
 * @param {Date} endDate - End date (inclusive)
 * @returns {Array} Filtered transactions
 */
export const filterTransactionsByDateRange = (transactions, startDate, endDate) => {
  return transactions.filter(transaction => {
    const transactionDate = new Date(transaction.timestamp);
    return transactionDate >= startDate && transactionDate <= endDate;
  });
};

/**
 * Gets transaction summary for a client
 * @param {Array} transactions - Array of transaction objects for a client
 * @returns {Object} Transaction summary
 */
export const getTransactionSummary = (transactions) => {
  const summary = {
    totalPayments: 0,
    totalAdjustments: 0,
    totalFees: 0,
    contactAttempts: 0,
    lastPaymentDate: null,
    lastPaymentAmount: 0,
    lastContactDate: null
  };
  
  transactions.forEach(transaction => {
    switch (transaction.type) {
      case TRANSACTION_TYPES.PAYMENT:
        summary.totalPayments += transaction.amount;
        if (!summary.lastPaymentDate || transaction.timestamp > summary.lastPaymentDate) {
          summary.lastPaymentDate = transaction.timestamp;
          summary.lastPaymentAmount = transaction.amount;
        }
        break;
      case TRANSACTION_TYPES.ADJUSTMENT:
        summary.totalAdjustments += transaction.amount;
        break;
      case TRANSACTION_TYPES.FEE:
      case TRANSACTION_TYPES.LEGAL_FEE:
      case TRANSACTION_TYPES.DEBT_FEE:
      case TRANSACTION_TYPES.INTEREST:
        summary.totalFees += transaction.amount;
        break;
      case TRANSACTION_TYPES.CONTACT:
        summary.contactAttempts++;
        if (!summary.lastContactDate || transaction.timestamp > summary.lastContactDate) {
          summary.lastContactDate = transaction.timestamp;
        }
        break;
    }
  });
  
  return summary;
};

/**
 * Validates a transaction object
 * @param {Object} transaction - Transaction object to validate
 * @returns {Object} Validation result with isValid boolean and errors array
 */
export const validateTransaction = (transaction) => {
  const errors = [];
  
  // Check required fields
  if (!transaction.clientId || transaction.clientId.trim().length === 0) {
    errors.push('Client ID is required');
  }
  
  if (!transaction.type) {
    errors.push('Transaction type is required');
  }
  
  if (!transaction.description || transaction.description.trim().length === 0) {
    errors.push('Transaction description is required');
  }
  
  // Validate transaction type
  if (transaction.type && !Object.values(TRANSACTION_TYPES).includes(transaction.type)) {
    errors.push('Invalid transaction type');
  }
  
  // Validate contact type for contact transactions
  if (transaction.type === TRANSACTION_TYPES.CONTACT) {
    if (!transaction.contactType || !Object.values(CONTACT_TYPES).includes(transaction.contactType)) {
      errors.push('Valid contact type is required for contact transactions');
    }
  }
  
  // Validate amount if present
  if (transaction.amount !== undefined && transaction.amount !== null && isNaN(transaction.amount)) {
    errors.push('Amount must be a valid number');
  }
  
  // Validate precision (no more than 2 decimal places)
  if (transaction.amount !== undefined && transaction.amount !== null && !isNaN(transaction.amount)) {
    const decimalPlaces = (transaction.amount.toString().split('.')[1] || '').length;
    if (decimalPlaces > 2) {
      errors.push('Amount cannot have more than 2 decimal places');
    }
  }
  
  // Validate description length
  if (transaction.description && transaction.description.length > TRANSACTION_VALIDATION_RULES.description.maxLength) {
    errors.push(`Description must be ${TRANSACTION_VALIDATION_RULES.description.maxLength} characters or less`);
  }
  
  // Validate timestamp
  if (transaction.timestamp && !(transaction.timestamp instanceof Date) && isNaN(new Date(transaction.timestamp))) {
    errors.push('Invalid timestamp format');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Rounds a number to 2 decimal places
 * @param {number} value - Number to round
 * @returns {number} Rounded number
 */
export const roundToTwoDecimals = (value) => {
  return Math.round((value + Number.EPSILON) * 100) / 100;
};

/**
 * Formats a monetary value for display
 * @param {number} value - Monetary value
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value || 0);
};

/**
 * Generates a unique transaction ID
 * @returns {string} Unique transaction ID
 */
const generateTransactionId = () => {
  return 'txn_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};